"use client";

import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { AdsterraNative } from "@/components/ads/adsterra-native";
import { PaidCampaignCard } from "@/components/ads/paid-campaign-card";
import { useMarketHint } from "@/components/market/market-hint-context";
import { NativeAd } from "@/components/monetization/native-ad";
import { SponsoredLabel } from "@/components/monetization/sponsored-label";
import { usePostHogReady } from "@/components/providers/posthog-provider";
import type { AdsterraNativeUnit } from "@/lib/ads/adsterra";
import {
	type PaidCampaignView,
	paidCampaignPlatform,
} from "@/lib/ads/campaigns";
import {
	displayFallbackSurface,
	reservedMinHeightClass,
} from "@/lib/ads/display-fallback";
import { adsenseVariantId } from "@/lib/ads/experiment";
import { ADVERTISE_FLAT_PLANS, formatIdrRb } from "@/lib/ads/pricing";
import { useAdsenseArm } from "@/lib/ads/use-adsense-arm";
import { adZoneOf } from "@/lib/ads/zones";
import { useAnalytics } from "@/lib/analytics/hooks";
import { slotTimingProps } from "@/lib/analytics/slot-timing";
import { trackMonetizationEvent } from "@/lib/analytics/track";
import { useSession } from "@/lib/auth/client";
import { useHydrated } from "@/lib/hydration/use-hydrated";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { fetchNavbarData } from "@/lib/user/navbar-data-client";

interface Props {
	slot: string;
	adsEnabled: boolean;
	/** Active paid campaign for this zone — renders above AdSense + house CTA. */
	paidCampaign?: PaidCampaignView | null;
	/**
	 * Zone guardrail: real AdSense may render only when this is true (informational
	 * slots). High-intent conversion slots pass false → always the first-party
	 * prompt, never third-party ads. See src/lib/ads/zones.ts.
	 */
	allowAdsense: boolean;
	adsenseClient: string | null;
	adsenseSlot: string | null;
	/**
	 * Adsterra Native Banner unit for this slot, or null when the slot is not
	 * Adsterra-eligible or the build vars are unset. Renders only after AdSense
	 * declines the slot — see `showAdsterra` below.
	 */
	adsterraUnit?: AdsterraNativeUnit | null;
	/**
	 * House CTA target + copy. Omit both for the default in-house "advertise here"
	 * CTA (/<locale>/advertise), used by slots with no specific house copy.
	 */
	href?: string;
	label?: string;
	adSpaceLabel: string;
	className: string;
	minHeight: number;
	locale?: string;
	/**
	 * "house" → primary CTA is the first-party prompt (fires `ad_click`).
	 * "pro_upsell" → primary CTA is a Pro pitch to /pricing (fires
	 * `ad_pro_upsell_clicked`) and the whole slot self-suppresses for Pro viewers,
	 * since the host page (job-detail) is static ISR with no server session.
	 */
	ctaKind?: "house" | "pro_upsell";
	/** Secondary "go ad-free → Pro" micro-link below a house CTA (feed-sidebar). */
	proLink?: { href: string; label: string } | null;
	/** Conversion slots show a secondary "advertise here →" link below the CTA. */
	showAdvertiseLink?: boolean;
	advertiseHref?: string;
	advertiseLabel?: string;
}

/**
 * Default house CTA: sell our own paid placement. Prices come from
 * `src/lib/ads/pricing.ts`, never hand-typed.
 */
function defaultAdvertiseCta(slot: string, locale?: string) {
	const isId = locale === "id";
	return {
		href: `/${isId ? "id" : "en"}/advertise?src=${slot}`,
		label: isId
			? `Pasang iklan di sini — mulai ${formatIdrRb(ADVERTISE_FLAT_PLANS.weekly.idr)}/minggu →`
			: `Advertise here — from $${ADVERTISE_FLAT_PLANS.weekly.usd}/week →`,
	};
}

export function AdSlotClient({
	slot,
	adsEnabled,
	paidCampaign = null,
	allowAdsense,
	adsenseClient,
	adsenseSlot,
	adsterraUnit = null,
	href: hrefProp,
	label: labelProp,
	adSpaceLabel,
	className,
	minHeight,
	locale,
	ctaKind = "house",
	proLink = null,
	showAdvertiseLink = false,
	advertiseHref,
	advertiseLabel,
}: Props) {
	const defaultCta = defaultAdvertiseCta(slot, locale);
	const href = hrefProp ?? defaultCta.href;
	const label = labelProp ?? defaultCta.label;
	const {
		trackAdSlotImpression,
		trackAdSlotClicked,
		trackAdProUpsellClicked,
		trackAdCspBlocked,
		trackAdSlotUnfilled,
	} = useAnalytics();
	const isReady = usePostHogReady();
	const rootRef = useRef<HTMLDivElement>(null);
	const impressionFired = useRef(false);
	const adPushed = useRef(false);
	// Set after hydration once Google reports the unit unfilled; flips the slot
	// to the house CTA / Adsterra path instead of leaving a blank box.
	const [adsenseUnfilled, setAdsenseUnfilled] = useState(false);

	// Pro suppression for a pure Pro-upsell slot (job-detail-bottom). That page is
	// static ISR so the server can't read the session → `isPro` is never passed.
	// Anon viewers are definitely not Pro (show immediately, no fetch). Only when a
	// session exists do we confirm plan via /api/user/navbar-data and hide the slot
	// so a paying member is never shown "go ad-free → Pro".
	const { data: session } = useSession();
	const [proSuppressed, setProSuppressed] = useState(false);
	useEffect(() => {
		if (ctaKind !== "pro_upsell" || !session?.user) return;
		let cancelled = false;
		fetchNavbarData()
			.then((d) => {
				if (!cancelled && d?.isPro) setProSuppressed(true);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [ctaKind, session?.user]);

	// Real AdSense shows to every market (2026-09-21: it was global-only, which
	// left the Indonesian audience — nearly all traffic — with a structural zero).
	// `market` is still resolved client-side by <MarketHintProvider> because the
	// host pages are ISR/edge-cached; it now only tags telemetry.
	const market = useMarketHint();
	// Gated on hydration for the same reason as AiApplyBadge: this renders
	// inside the feed's streamed <Suspense> boundary, so the server HTML is the
	// house prompt and the AdSense <ins> may only replace it after hydration.
	// Swapping on the first client render would trip React #418 for the whole
	// boundary.
	const hydrated = useHydrated();
	// Anti-cannibalization zone for this slot — tags ad telemetry so
	// informational vs conversion performance can be split in PostHog.
	const zone = adZoneOf(slot);

	// AdSense placement experiment (see `@/lib/ads/experiment`): which arm this
	// visitor is in, from the PostHog flag + payload overrides. `capable` = a real
	// AdSense unit could render here at all; only then is the visitor part of the
	// experiment and only then are events labelled with an arm.
	const arm = useAdsenseArm(slot, market);
	const adsenseCapable =
		allowAdsense && !!adsenseClient && !!adsenseSlot && adsEnabled;
	// Held until the arm resolves so an unresolved visitor is never mounted into
	// AdSense and then withdrawn (that already spends an ad request).
	const adsensePending = adsenseCapable && !arm.ready;
	const armLabel = adsenseCapable && arm.ready ? arm.label : undefined;
	const armVariantId =
		adsenseCapable && arm.ready
			? adsenseVariantId(arm.label, market)
			: undefined;

	// Paid campaign wins the zone (advertiser paid for it) over AdSense + house.
	const showPaid = !!paidCampaign;
	const paidPlatform = paidCampaign
		? paidCampaignPlatform(paidCampaign.id)
		: undefined;

	const showRealAd =
		!showPaid &&
		adsEnabled &&
		allowAdsense &&
		!!adsenseClient &&
		!!adsenseSlot &&
		hydrated &&
		arm.ready &&
		arm.allowed &&
		!adsenseUnfilled;

	// Adsterra fills only what AdSense declines, so the two can never stack in
	// one slot. Adsterra only becomes reachable on slots that sit past the
	// conversion point (see `isAdsterraEligibleSlot`).
	const adsterraEligible =
		!showPaid && !showRealAd && !adsensePending && adsEnabled && !!adsterraUnit;

	// Adsterra draws on the SAME session budget as the first-party affiliate
	// cards, via the guardrail layer those cards already run through
	// (`useAdGuardrails` → `canShowUnit`). Until now `AdSlot` bypassed it
	// entirely: a blog post rendered two display slots and up to three affiliate
	// units with no shared accounting, and on 2026-09-02 Adsterra took 100% of
	// every eligible slot — including `job-detail-bottom`, whose Pro upsell is
	// worth more than a $0.5–2.8 CPM impression.
	//
	// Routing it through the same gate makes the split dynamic instead of
	// hardcoded: `maxDisplay` is 0 for a shallow anon reader (they get the house
	// CTA, never a network ad), 1–2 deeper in the session, 0 for Pro, and the
	// shared `sessionImpressionCap` means every Adsterra impression spends
	// budget an affiliate card would otherwise have had. All of it is live-tunable
	// from the `monetization-settings` PostHog payload with no deploy.
	const displayGuard = useAdGuardrails(undefined, "display");
	const displayBlocked = displayGuard.ready && displayGuard.blocked;

	// Whether Adsterra actually put a creative in its container: `null` while the
	// unit is still deciding, `true` on a fill, `false` on any no-fill (consent
	// withheld, container already claimed by a sibling slot, script error, or the
	// network simply having nothing to serve).
	//
	// Before this existed the Adsterra branch was terminal: it rendered INSTEAD of
	// the house CTA, so every no-fill left a reserved 280px hole. That is what
	// shipped — on 2026-09-02 the three eligible slots went 100% Adsterra / 0%
	// house, and the `job-detail-bottom` Pro upsell was displaced by an empty box.
	const [adsterraFilled, setAdsterraFilled] = useState<boolean | null>(null);

	// `useAdGuardrails` returns a fresh object every render, so the recorder is
	// held in a ref — a callback identity that churns would restart the loader.
	const recordDisplayImpression = displayGuard.recordImpression;
	const recordDisplayImpressionRef = useRef(recordDisplayImpression);
	recordDisplayImpressionRef.current = recordDisplayImpression;

	const handleAdsterraResolved = useCallback((filled: boolean) => {
		setAdsterraFilled(filled);
		// Only a creative that actually rendered spends session budget. A no-fill
		// must not consume the slot an affiliate card could have used.
		if (filled) recordDisplayImpressionRef.current();
	}, []);

	const handleAdsterraCspBlocked = useCallback(
		(hosts: string[]) => {
			trackAdCspBlocked({ slot, hosts, locale });
		},
		[slot, locale, trackAdCspBlocked],
	);

	// A capped viewer never mounts the unit, so nothing would ever resolve it —
	// settle it here so the slot falls through to the house CTA and the
	// impression below is not held forever waiting on a unit that will not run.
	useEffect(() => {
		if (displayBlocked) setAdsterraFilled(false);
	}, [displayBlocked]);

	const showAdsterra =
		adsterraEligible && !displayBlocked && adsterraFilled !== false;

	// One resolved slot type for BOTH the impression and the click. They used to
	// be computed independently and the click paths hardcoded
	// `showRealAd ? "adsense" : "placeholder"`, so an Adsterra-era click was
	// logged as `placeholder` and could never be joined to its own impression.
	//
	// `adsterra` requires a CONFIRMED fill, not merely the branch rendering.
	// Counting a blank container as an Adsterra impression made the rail
	// unmeasurable: the 54 lifetime rows recorded on 2026-09-02/03 are an upper
	// bound on ads shown, not a count of them.
	const renderedSlotType = showRealAd
		? "adsense"
		: adsterraEligible && adsterraFilled === true
			? "adsterra"
			: "placeholder";

	useEffect(() => {
		if (!isReady || impressionFired.current) return;
		// Hold until the AdSense arm resolves so the event carries the right arm.
		if (adsensePending) return;
		// Hold the impression until the unit has resolved, so `slot_type` records
		// what the visitor actually saw rather than which branch was attempted.
		if (adsterraEligible && adsterraFilled === null) return;
		const el = rootRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && !impressionFired.current) {
					impressionFired.current = true;
					const slotType = renderedSlotType;
					trackAdSlotImpression(slot, {
						locale,
						ads_enabled: adsEnabled,
						slot_type: slotType,
						market,
						zone: zone ?? "unknown",
						arm: armLabel,
						// When and where on the page the slot became viewable — the
						// numbers that make "it painted after I scrolled past" measurable.
						...slotTimingProps(entry),
					});
					trackMonetizationEvent({
						eventKind: "ad_impression",
						variantId: armVariantId,
						slot,
						slotType,
						adsEnabled,
						locale,
						// Paid campaigns join impression→click by platform (paid:<id>).
						platform: paidPlatform,
					});
				}
			},
			{ threshold: 0.5 },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [
		isReady,
		slot,
		locale,
		adsEnabled,
		adsterraEligible,
		adsterraFilled,
		renderedSlotType,
		paidPlatform,
		market,
		zone,
		adsensePending,
		armLabel,
		armVariantId,
		trackAdSlotImpression,
	]);

	// Push the unit to AdSense once, only when a real ad is actually rendered.
	useEffect(() => {
		if (!showRealAd || adPushed.current) return;
		adPushed.current = true;
		try {
			const w = window as unknown as { adsbygoogle?: unknown[] };
			if (!w.adsbygoogle) w.adsbygoogle = [];
			w.adsbygoogle.push({});
		} catch {
			// adsbygoogle script not yet loaded — the loader retries on its own.
		}
	}, [showRealAd]);

	// Watch the pushed unit for an unfilled verdict. AdSense stamps
	// `data-ad-status="unfilled"` on the <ins> (and `data-adsbygoogle-status=done`
	// once it processed it). Only runs post-hydration (showRealAd requires it), so
	// the swap below never touches server HTML. A filled unit is left alone.
	const unfilledReported = useRef(false);
	useEffect(() => {
		if (!showRealAd) return;
		const ins = rootRef.current?.querySelector("ins.adsbygoogle");
		if (!ins) return;
		const markUnfilled = (reason: "status" | "timeout") => {
			if (!unfilledReported.current) {
				unfilledReported.current = true;
				trackAdSlotUnfilled({
					slot,
					locale,
					market,
					zone: zone ?? "unknown",
					reason,
				});
			}
			setAdsenseUnfilled(true);
		};
		let timer: ReturnType<typeof setTimeout> | undefined;
		const evaluate = () => {
			const status = ins.getAttribute("data-ad-status");
			if (status === "unfilled") {
				markUnfilled("status");
				return true;
			}
			return status === "filled";
		};
		if (evaluate()) return;
		const observer = new MutationObserver(() => {
			if (evaluate()) {
				observer.disconnect();
				clearTimeout(timer);
			}
		});
		observer.observe(ins, {
			attributes: true,
			attributeFilter: ["data-ad-status", "data-adsbygoogle-status"],
		});
		// Close the gap between the first evaluate() and observe(): a verdict
		// stamped in between would otherwise never fire a mutation record.
		if (evaluate()) {
			observer.disconnect();
			return;
		}
		timer = setTimeout(() => {
			// No verdict after 6 s and no creative in the unit. This used to also
			// require `data-adsbygoogle-status="done"`, so a loader that never
			// processed the unit (blocked script, adblock, CSP) left the reserved
			// box blank forever. An iframe means a creative is on its way in.
			if (!ins.getAttribute("data-ad-status") && !ins.querySelector("iframe")) {
				markUnfilled("timeout");
			}
		}, 6000);
		return () => {
			observer.disconnect();
			clearTimeout(timer);
		};
	}, [showRealAd, slot, locale, market, zone, trackAdSlotUnfilled]);

	if (proSuppressed) return null;

	if (showPaid && paidCampaign) {
		const c = paidCampaign;
		return (
			<div ref={rootRef} className={className}>
				<PaidCampaignCard
					campaign={c}
					slot={slot}
					locale={locale}
					onCtaClick={() => {
						trackAdSlotClicked(slot, {
							locale,
							market,
							zone: zone ?? "unknown",
							arm: armLabel,
						});
						trackMonetizationEvent({
							eventKind: "ad_click",
							variantId: armVariantId,
							slot,
							slotType: renderedSlotType,
							locale,
							platform: paidPlatform,
						});
					}}
				/>
			</div>
		);
	}

	if (showRealAd) {
		return (
			<div
				// Keyed so the unfilled swap REMOUNTS the wrapper instead of reusing it:
				// Google writes inline `height:0` onto the <ins>'s parent when a
				// full-width-responsive unit is unfilled, and React only reverts the
				// props it owns (`minHeight`), so a reused div kept the house CTA in a
				// 0px box (prod 2026-09-21: blog-top 640x0).
				key="adsense-unit"
				ref={rootRef}
				className={`ad-slot ad-slot--${slot} ${className}`}
				style={{ minHeight }}
				data-slot={slot}
			>
				{/* AdSense allows "Advertisement/Sponsored" labelling on its own
				    units. This slot previously carried no disclosure at all — every
				    other resolution path (paid campaign, Adsterra, house CTA) does. */}
				<div className="mb-1 flex items-center justify-between">
					<SponsoredLabel
						locale={locale === "en" ? "en" : "id"}
						advertiseHref={`/${locale === "en" ? "en" : "id"}/advertise?src=${slot}`}
					/>
				</div>
				<ins
					className="adsbygoogle"
					style={{ display: "block", minHeight }}
					data-ad-client={adsenseClient ?? undefined}
					data-ad-slot={adsenseSlot ?? undefined}
					data-ad-format="auto"
					data-full-width-responsive="true"
				/>
			</div>
		);
	}

	// The house CTA — the first-party unit every fallback path lands on. Built
	// once here so the Adsterra branch can paint it FIRST and only swap it out
	// for a creative that actually filled.
	const houseCta = (
		<>
			<Link
				href={href}
				onClick={() => {
					// Pro-upsell slots feed the /pricing funnel, so the PostHog event
					// stays split: `ad_pro_upsell_clicked` for the upsell, `ad_slot_clicked`
					// for a plain house CTA.
					//
					// The D1 `ad_click` beacon fires for BOTH. #1649 added the per-slot CTR
					// breakdown (`getAdAffiliateSummary` in
					// `@/lib/db/queries/ad-affiliate-telemetry`, rendered by the admin
					// monetization page) and, in the same PR, routed this slot's click off
					// `ad_click` — leaving a numerator of 0 against a live impression
					// denominator. The admin dashboard therefore reported job-detail-bottom
					// at 0.0% CTR for 78 days while `ad_pro_upsell_clicked`, the only event
					// that did fire, had no reader anywhere in the repo. The CTR denominator
					// is `ad_impression` ON THIS SLOT, so its numerator must be every click
					// on this slot regardless of where the click goes.
					if (ctaKind === "pro_upsell") {
						trackAdProUpsellClicked({
							surface: slot,
							locale,
							market,
							zone: zone ?? "unknown",
							arm: armLabel,
						});
					} else {
						trackAdSlotClicked(slot, {
							locale,
							market,
							zone: zone ?? "unknown",
							arm: armLabel,
							...(href.includes("/advertise") ? { intent: "advertise" } : {}),
						});
					}
					trackMonetizationEvent({
						eventKind: "ad_click",
						variantId: armVariantId,
						slot,
						slotType: renderedSlotType,
						locale,
					});
				}}
				className="group flex items-center justify-between gap-4 rounded-2xl border border-border/40 bg-card px-5 py-4 transition-all hover:border-border/60 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			>
				<div className="min-w-0 space-y-0.5">
					<p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
						{adSpaceLabel}
					</p>
					<p className="text-sm font-medium text-foreground/70 transition-colors group-hover:text-foreground">
						{label}
					</p>
				</div>
				<ArrowUpRight
					className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-muted-foreground"
					aria-hidden="true"
				/>
			</Link>
			{proLink ? (
				<div className="mt-1.5 text-right">
					<Link
						href={proLink.href}
						onClick={() =>
							trackAdProUpsellClicked({
								surface: slot,
								locale,
								market,
								zone: zone ?? "unknown",
								arm: armLabel,
							})
						}
						className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
					>
						{proLink.label}
					</Link>
				</div>
			) : showAdvertiseLink && advertiseHref ? (
				<div className="mt-1.5 text-right">
					<Link
						href={advertiseHref}
						onClick={() => {
							trackMonetizationEvent({
								eventKind: "advertise_slot_link_clicked",
								slot,
								locale,
							});
						}}
						className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
					>
						{advertiseLabel}
					</Link>
				</div>
			) : null}
		</>
	);

	// Fallback ladder, after paid campaign -> AdSense -> Adsterra: the affiliate
	// engine, then (only if it returns nothing) the house card above. Reached only
	// once every network path this slot attempted has ended unfilled / timed out /
	// blocked — `adsenseUnfilled` for AdSense, `adsterraFilled === false` when
	// Adsterra is next in line (it also covers a capped viewer who never mounted
	// it). A pure Pro-upsell slot keeps its own CTA (that IS its paid conversion,
	// not a hole). Pro viewers see nothing: `NativeAd` runs the same guardrail as
	// every other affiliate unit and collapses for them.
	//
	// One request -> one terminal: `NativeAd` owns the whole request lifecycle
	// (`useSlotRequestTerminal`), mounts only after this decision, and this branch
	// carries no `rootRef`, so the placeholder `ad_slot_impression` is not fired
	// alongside its `affiliate_impression`.
	const displayFallback =
		!showPaid &&
		!showRealAd &&
		!adsensePending &&
		ctaKind !== "pro_upsell" &&
		(adsterraEligible ? adsterraFilled === false : adsenseUnfilled);
	if (displayFallback) {
		return (
			<div
				className={className}
				data-slot={slot}
				data-display-fallback="affiliate"
			>
				<NativeAd
					surface={displayFallbackSurface(slot)}
					locale={locale === "en" ? "en" : "id"}
					minHeightClass={reservedMinHeightClass(minHeight)}
					emptyFallback={houseCta}
				/>
			</div>
		);
	}

	if (showAdsterra && adsterraUnit) {
		// CTA-first (2026-09-05). This branch used to render the "Sponsored"
		// eyebrow and nothing else until the guardrail (a session round-trip),
		// consent/region, an idle callback, the frame load and a 3–4 s fill
		// verdict had ALL cleared — then, on the usual no-fill, the house CTA.
		// Server HTML and first paint were an empty box, and on a page where
		// the slot sits ~19 blocks down the reader had scrolled past before the
		// chain finished. Now the CTA is in the server HTML and stays until
		// Adsterra confirms a fill; the loader runs beside it, invisible but
		// laid out at full size so the network can measure the container.
		const filled = adsterraFilled === true;
		return (
			<div
				ref={rootRef}
				className={`ad-slot ad-slot--${slot} relative ${className}`}
				style={{ minHeight }}
				data-slot={slot}
				data-adsterra={filled ? "filled" : "pending"}
			>
				{filled ? (
					<SponsoredLabel
						locale={locale === "en" ? "en" : "id"}
						advertiseHref={`/${locale === "en" ? "en" : "id"}/advertise?src=${slot}`}
					/>
				) : (
					houseCta
				)}
				{displayGuard.ready ? (
					<div
						className={filled ? "block" : "invisible absolute inset-0"}
						style={{ minHeight }}
						aria-hidden={!filled}
					>
						<AdsterraNative
							onResolved={handleAdsterraResolved}
							onCspBlocked={handleAdsterraCspBlocked}
						/>
					</div>
				) : null}
			</div>
		);
	}

	return (
		<div ref={rootRef} className={className} data-slot={slot}>
			{houseCta}
		</div>
	);
}
